// Apolo IDE — DiffCard (Code chat redesign v2 — Codex-rich).
//
// Renderiza UM arquivo que o agente editou/criou como um cartão rico, portado
// pixel-a-pixel do mockup _mockups/code-chat-v2/redesign.html (.diffcard):
// header com ícone glow-verde, basename em negrito, o delta mono
// (+adições / −remoções) e a pasta + status como subline; ações Desfazer /
// Revisar à direita; a barra de diff colorida (verde/vermelho proporcional);
// e um rodapé "Detalhes" colapsável com o diff cru.
//
//   [▤] SPRINT-PLAN.md  +280 −119          [↩ Desfazer] [Revisar]
//       editado · reescrita pro formato da skill
//   ▰▰▰▰▰▰▰▰▰▰▰▱▱▱
//   ▾ Detalhes
//
//   • Estrutura espelha o mockup usando as classes `rx-*` de redesign.css.
//   • Delta: +N em .rx-a (verde glow), −N em .rx-d (vermelho) — este só
//     quando deletions > 0.
//   • "Desfazer" é stub: sem onDesfazer vira disabled com title "em breve".
//   • "Detalhes" só aparece quando diffText é string não-vazia; fechado por
//     padrão; toggla um <pre> mono em .rx-diffbody. aria-expanded acompanha.
//
// Props:
//   { path, additions = 0, deletions = 0, status = 'done',
//     diffText, onRevisar, onDesfazer }

import { useState } from 'react';
import { ChevronDown, ChevronRight, FilePen, Undo2 } from 'lucide-react';
import './redesign.css';

const STATUS_LABEL = {
  done: 'editado',
  new: 'novo',
  pending: 'pendente',
};

export default function DiffCard({
  path,
  additions = 0,
  deletions = 0,
  status = 'done',
  diffText,
  onRevisar,
  onDesfazer,
}) {
  const [open, setOpen] = useState(false);

  const safePath = String(path ?? '').replace(/\\/g, '/');
  const basename = safePath.split('/').pop() || safePath;
  const dir = safePath.slice(0, safePath.length - basename.length).replace(/\/$/, '');
  const statusLabel = STATUS_LABEL[status] || status;
  const subline = dir ? `${dir} · ${statusLabel}` : statusLabel;

  const hasDiff = typeof diffText === 'string' && diffText.length > 0;

  return (
    <div className="rx-diffcard">
      <div className="rx-diffcard-h">
        <span className="rx-dic">
          <FilePen size={18} strokeWidth={1.9} aria-hidden="true" />
        </span>

        <div className="rx-dc-t">
          <div className="rx-name" title={basename}>
            {basename}
            <span
              className="rx-delta"
              aria-label={`${additions} adições, ${deletions} remoções`}
            >
              <span className="rx-a">+{additions}</span>
              {deletions > 0 && <span className="rx-d">−{deletions}</span>}
            </span>
          </div>
          {subline && (
            <div className="rx-sub" title={safePath}>
              {subline}
            </div>
          )}
        </div>

        <div className="rx-dc-act">
          {onDesfazer ? (
            <button type="button" className="rx-btn" onClick={() => onDesfazer()}>
              <Undo2 size={14} strokeWidth={2} aria-hidden="true" />
              Desfazer
            </button>
          ) : (
            <button type="button" className="rx-btn" disabled title="Desfazer — em breve">
              <Undo2 size={14} strokeWidth={2} aria-hidden="true" />
              Desfazer
            </button>
          )}
          <button
            type="button"
            className="rx-btn rx-btn-primary"
            onClick={() => onRevisar?.()}
          >
            Revisar
          </button>
        </div>
      </div>

      <div className="rx-diffbar">
        <i className="rx-ad" style={{ flex: additions || 1 }} />
        {deletions > 0 && <i className="rx-de" style={{ flex: deletions }} />}
      </div>

      {hasDiff && (
        <>
          <button
            type="button"
            className="rx-detalhes"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? (
              <ChevronDown size={13} aria-hidden="true" />
            ) : (
              <ChevronRight size={13} aria-hidden="true" />
            )}
            Detalhes
          </button>
          {open && (
            <pre className="rx-diffbody" style={{ margin: 0, padding: '10px 15px' }}>
              {diffText}
            </pre>
          )}
        </>
      )}
    </div>
  );
}
